import { useState } from 'react'
import { Plus, Sparkles } from 'lucide-react'
import { AssignmentSelector } from './AssignmentSelector'
import { QuickScanCamera } from '../parent/QuickScanCamera'
import { StudentQuickScanResult } from './StudentQuickScanResult'

interface ExpectedAssignment {
  id: number
  source_type: string
  name: string
  subject: string | null
  course_name: string | null
}

type ScanState = 'idle' | 'select-assignment' | 'camera' | 'result'

export function StudentQuickScan() {
  const [state, setState] = useState<ScanState>('idle')
  const [selectedAssignment, setSelectedAssignment] = useState<ExpectedAssignment | null>(null)
  const [capturedImages, setCapturedImages] = useState<string[]>([])
  const [showToast, setShowToast] = useState(false)
  const [lastXpAwarded, setLastXpAwarded] = useState(0)

  const handleFabClick = () => {
    setState('select-assignment')
  }

  const handleSelectAssignment = (assignment: ExpectedAssignment | null) => {
    setSelectedAssignment(assignment)
    setState('camera')
  }

  const handleCapture = (imageData: string) => {
    setCapturedImages(prev => [...prev, imageData])
    setState('result')
  }

  const handleConfirm = (xpAwarded: number) => {
    // Reset state
    setState('idle')
    setSelectedAssignment(null)
    setCapturedImages([])

    // Show success toast
    setLastXpAwarded(xpAwarded)
    setShowToast(true)
    setTimeout(() => setShowToast(false), 3000)
  }

  const handleRetake = () => {
    // Remove last captured image and go back to camera
    setCapturedImages(prev => prev.slice(0, -1))
    setState('camera')
  }

  const handleCancel = () => {
    setState('idle')
    setSelectedAssignment(null)
    setCapturedImages([])
  }

  const handleAddPage = () => {
    setState('camera')
  }

  return (
    <>
      {/* Floating Action Button - always visible when idle */}
      {state === 'idle' && (
        <button
          onClick={handleFabClick}
          aria-label="Quick Scan"
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-quest-primary hover:bg-quest-primary/90 text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center group"
        >
          <Plus className="w-7 h-7 group-hover:scale-110 transition-transform" />
          <span className="absolute right-full mr-3 px-3 py-1.5 bg-surface-card text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            Scan Homework
          </span>
        </button>
      )}

      {/* Assignment selector overlay */}
      {state === 'select-assignment' && (
        <AssignmentSelector
          onSelect={handleSelectAssignment}
          onClose={handleCancel}
        />
      )}

      {/* Camera overlay */}
      {state === 'camera' && (
        <QuickScanCamera
          onCapture={handleCapture}
          onClose={() => {
            if (capturedImages.length > 0) {
              setState('result')
            } else {
              // Go back to assignment selector instead of closing
              setState('select-assignment')
            }
          }}
          pageNumber={capturedImages.length + 1}
          existingPages={capturedImages.length}
        />
      )}

      {/* Result overlay - instant preview, no waiting */}
      {state === 'result' && capturedImages.length > 0 && (
        <StudentQuickScanResult
          images={capturedImages}
          preSelectedAssignment={selectedAssignment}
          onConfirm={handleConfirm}
          onRetake={handleRetake}
          onCancel={handleCancel}
          onAddPage={handleAddPage}
        />
      )}

      {/* Success toast with XP */}
      {showToast && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 bg-gradient-to-r from-quest-primary to-orange-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in">
          <Sparkles className="w-5 h-5" />
          <span className="font-medium">+{lastXpAwarded} XP</span>
          <span>•</span>
          <span>Saved!</span>
        </div>
      )}
    </>
  )
}
